import * as React from 'react';
import {useNavigate, Outlet} from "react-router";
import {styled} from "@mui/material/styles";
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar, {AppBarProps as MuiAppBarProps} from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {RouterBreadcrumbs, DescriptionAlert, PasswordDialog} from '../share';
import {mainListItems} from './listItems';
import {useStateSession} from "../hooks/useAuth";
import {useTitle} from 'react-use';
import Footer from "../share/components/footer/Footer.tsx";

import {secondaryListItems as authSecondaryListItems} from '../auth/pages/listItems';
import {secondaryListItems as locationSecondaryListItems} from '../location/pages/listItems';
import {secondaryListItems as addressSecondaryListItems} from '../address/pages/listItems';
import {secondaryListItems as timberSecondaryListItems} from '../timber/pages/listItems';


const drawerWidth: number = 270;

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({theme, open}) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                width: theme.spacing(7),
                [theme.breakpoints.up('sm')]: {
                    width: theme.spacing(9),
                },
            }),
            // Hide completely for smaller screens
            [theme.breakpoints.down('md')]: {
                display: 'none',
            },
        },
    }),
);

const DashboardPage = () => {

    useTitle('Dashboard');
    
    const [open, setOpen] = React.useState(true);
    const toggleDrawer = () => {
        setOpen(!open);
    };

    const [expanded, setExpanded] = React.useState<string>("timber");
    const _handlePress = React.useCallback((packageName: string) => {
        setExpanded(packageName);
    })

    const navigate = useNavigate()

    const session = useStateSession()

    return (
        <Box sx={ {display: 'flex'} }>
            <Drawer variant="permanent" open={open}>
                <Toolbar
                    sx={ {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        px: [1],
                    } }
                >
                    <IconButton onClick={toggleDrawer}>
                        {open ? <ChevronLeftIcon/> : <ChevronRightIcon/>}
                    </IconButton>
                </Toolbar>
                <Divider/>
                { session &&
                    <List component="nav">
                        { mainListItems(navigate) }
                        { timberSecondaryListItems(expanded === 'timber', session, _handlePress) }
                        { addressSecondaryListItems(expanded === 'address', session, _handlePress) }
                        { locationSecondaryListItems(expanded === 'location', session, _handlePress) }
                        { authSecondaryListItems(expanded === 'auth', session, _handlePress) }
                        
                    </List>
                }
            </Drawer>
            <Box
                component="main"
                sx={ {
                    backgroundColor: (theme) => theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'auto',
                } }
            >
                <PasswordDialog/>
                <Paper sx={ { 
                    padding: "0.5em",
                    position: "sticky", 
                    top: 0, 
                    zIndex: 1000, 
                    backdropFilter: "blur(2px)", 
                } }>
                    <RouterBreadcrumbs/>
                    <DescriptionAlert/>
                </Paper>
                <Container maxWidth="false" disableGutters>
                    <Outlet/>
                </Container>
                <Divider/>
                <Footer/>
            </Box>
        </Box>
    );
}

export default DashboardPage;
